/**
 * 보안 메모 상세보기 모달
 */

import React, { useState } from 'react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { 
  X, 
  Calendar, 
  Lock, 
  Heart, 
  Archive, 
  Edit,
  Tag,
  Clock,
  FileText,
  Download,
  Eye
} from 'lucide-react';
import { Button } from '../UI';
import './SecureNoteViewModal.css';

function SecureNoteViewModal({ 
  note, 
  isOpen, 
  onClose, 
  onEdit,
  onToggleFavorite,
  onToggleArchive 
}) {
  const [showRawContent, setShowRawContent] = useState(false);

  if (!isOpen || !note) return null;

  // 날짜 포맷팅
  const formatDate = (date) => {
    if (!date) return '';
    
    try {
      // Firestore Timestamp 또는 일반 Date 객체 처리
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      
      // 유효한 날짜인지 확인
      if (isNaN(dateObj.getTime())) {
        console.warn('Invalid date:', date);
        return '';
      }
      
      return format(dateObj, 'yyyy년 M월 d일 HH:mm', { locale: ko });
    } catch (error) {
      console.error('Date formatting error:', error, date);
      return '';
    }
  };

  // 카테고리 색상
  const getCategoryColor = () => {
    switch (note.category) {
      case 'personal': return '#6c757d';
      case 'work': return '#007bff';
      case 'finance': return '#28a745';
      case 'health': return '#dc3545';
      default: return '#6c757d';
    }
  };

  // 첨부파일 보기 (새 탭에서 열기)
  const handleViewAttachment = (attachment) => {
    if (attachment.url) {
      // 그냥 URL로 열기
      window.open(attachment.url, '_blank', 'noopener,noreferrer');
    }
  };
  
  // 첨부파일 다운로드
  const handleDownloadAttachment = (attachment) => {
    if (attachment.url) {
      const link = document.createElement('a');
      link.href = attachment.url;
      link.download = attachment.originalName || attachment.name || 'attachment';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="note-view-modal" onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <div className="note-view-header">
          <div className="note-view-title">
            <div className="title-section">
              <h2>{note.title}</h2>
              {note.isPinProtected && <Lock size={18} className="pin-icon" />}
            </div>
            
            <div className="note-badges">
              {note.category && (
                <span 
                  className="category-badge"
                  style={{ backgroundColor: getCategoryColor() }}
                >
                  {note.category}
                </span>
              )}
              
              {note.isFavorite && (
                <Heart size={16} className="favorite-icon filled" />
              )}
              
              {note.isArchived && (
                <Archive size={16} className="archive-icon" />
              )}
            </div>
          </div>
          
          <div className="note-view-actions">
            <Button
              variant="ghost"
              icon="edit"
              onClick={onEdit}
              title="편집"
            />
            
            <Button
              variant="ghost"
              icon="❤️"
              onClick={onToggleFavorite}
              title={note.isFavorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
              className={note.isFavorite ? 'favorite-active' : ''}
            />
            
            <Button
              variant="ghost"
              icon="📁"
              onClick={onToggleArchive}
              title={note.isArchived ? '아카이브 해제' : '아카이브'}
            />
            
            <Button
              variant="ghost"
              icon="x"
              onClick={onClose}
              title="닫기"
            />
          </div>
        </div>

        {/* 메타 정보 */}
        <div className="note-meta">
          <div className="meta-item">
            <Calendar size={14} />
            <span>생성: {formatDate(note.createdAt)}</span>
          </div>
          
          <div className="meta-item">
            <Clock size={14} />
            <span>수정: {formatDate(note.updatedAt)}</span>
          </div>
          
          {note.lastAccessedAt && (
            <div className="meta-item">
              <FileText size={14} />
              <span>최종 조회: {formatDate(note.lastAccessedAt)}</span>
            </div>
          )}
        </div>

        {/* 태그 */}
        {note.tags && note.tags.length > 0 && (
          <div className="note-tags">
            <Tag size={14} />
            <div className="tags-list">
              {note.tags.map((tag, index) => (
                <span key={index} className="tag">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* 내용 */}
        <div className="note-content">
          {showRawContent ? (
            <pre className="raw-content">{note.plainTextContent}</pre>
          ) : (
            <div 
              className="formatted-content"
              dangerouslySetInnerHTML={{ __html: note.content }}
            />
          )}
        </div>

        {/* 첨부파일 */}
        {note.attachments && note.attachments.length > 0 && (
          <div className="note-attachments">
            <h4>첨부파일</h4>
            <div className="attachments-list">
              {note.attachments.map((attachment, index) => (
                <div key={index} className="attachment-item">
                  <div className="attachment-info">
                    <span className="attachment-name">
                      {attachment.originalName || attachment.name}
                    </span>
                    <span className="attachment-size">
                      {Math.round(attachment.size / 1024)}KB
                    </span>
                  </div>
                  
                  <div className="attachment-actions">
                    <Button
                      variant="ghost"
                      icon="eye"
                      onClick={() => handleViewAttachment(attachment)}
                      title="새 탭에서 보기"
                      size="sm"
                    />
                    <Button
                      variant="ghost"
                      icon="download"
                      onClick={() => handleDownloadAttachment(attachment)}
                      title="다운로드"
                      size="sm"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 하단 컨트롤 */}
        <div className="note-view-footer">
          <div className="content-toggle">
            <Button
              variant="ghost"
              onClick={() => setShowRawContent(!showRawContent)}
              size="sm"
            >
              {showRawContent ? '서식 보기' : '원본 보기'}
            </Button>
          </div>
          
          <div className="footer-actions">
            <Button
              variant="secondary"
              onClick={onEdit}
              size="sm"
            >
              편집
            </Button>

            <Button
              variant="primary"
              onClick={onClose}
              size="sm"
            >
              확인
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SecureNoteViewModal;